import memoize from 'lodash/memoize'
import { ContextApi } from '@pancakeswap/localization'
import { CAD1_LIVE } from 'views/Cad1/config'
import { PageMeta } from './types'
import { ShareCardSlug, canonicalUrl, ogImageUrl } from './seo'

/**
 * Title, description, share card and canonical URL per route, read on the server by <PageMeta /> (rendered in _app, outside the
 * PersistGate, so crawlers see it). 2026-10-03: rewritten for what is live now; the PancakeSwap-era entries (Limit Orders, IFO,
 * Pottery, Teams, NFT market, Farm Auctions, Prediction, Presale) are gone because those paths only redirect.
 * Titles stay under 60 characters with the " | CADINUdex" suffix, descriptions under 160 (seo.test.ts checks both).
 */

export const DEFAULT_DESCRIPTION =
  'CADINUdex on BNB Chain: swap tokens, stake for CBON rewards, run trading competitions and lock liquidity anyone can verify.'

export const DEFAULT_META: PageMeta = {
  title: 'CADINUdex',
  description: DEFAULT_DESCRIPTION,
  image: ogImageUrl('home'),
}

interface PathMeta {
  title: string
  description?: string
  card?: ShareCardSlug
  /** also matches every pathname that starts with this one (sub-pages); a base match gets no canonical URL */
  basePath?: boolean
  /** the public URL path when it differs from the Next pathname (rewritten routes) */
  publicPath?: string
}

interface PathList {
  paths: { [path: string]: PathMeta }
  defaultTitleSuffix: string
}

const COMPETITIONS: PathMeta = {
  title: 'Trading Competitions',
  description: 'Token trading competitions on BNB Chain: trade or hold during the window, climb the leaderboard, and the top traders share the prize pool.',
  card: 'competitions',
  publicPath: '/trading-competition',
}
const CREATE_COMPETITION: PathMeta = {
  title: 'Create a Trading Competition',
  description: 'Launch a trading competition for your token: pick the pool, set the prize and the number of winners, and fund it from your wallet.',
  card: 'create-competition',
  publicPath: '/trading-competition/create',
}
const LIQUIDITY_DESC = 'Add tokens to CADINUdex pools on BNB Chain and earn a share of the trading fees.'

const getPathList = (t: ContextApi['t']): PathList => {
  return {
    paths: {
      '/': { title: t('Swap, earn and compete on BNB Chain'), description: DEFAULT_DESCRIPTION, card: 'home' },
      '/swap': {
        basePath: true,
        title: t('Swap tokens on BNB Chain'),
        description: 'Swap BEP-20 tokens on CADINUdex at the best route across BNB Chain pools, straight from your own wallet.',
        card: 'swap',
      },
      '/liquidity': { basePath: true, title: t('Liquidity'), description: LIQUIDITY_DESC, card: 'liquidity' },
      '/add': { basePath: true, title: t('Add Liquidity'), description: LIQUIDITY_DESC, card: 'liquidity' },
      '/v2/add': { basePath: true, title: t('Add Liquidity'), description: LIQUIDITY_DESC, card: 'liquidity' },
      '/increase': { basePath: true, title: t('Increase Liquidity'), description: LIQUIDITY_DESC, card: 'liquidity' },
      '/remove': { basePath: true, title: t('Remove Liquidity'), description: LIQUIDITY_DESC, card: 'liquidity' },
      '/v2/remove': { basePath: true, title: t('Remove Liquidity'), description: LIQUIDITY_DESC, card: 'liquidity' },
      '/v2/pair': { basePath: true, title: t('Liquidity'), description: LIQUIDITY_DESC, card: 'liquidity' },
      '/find': { title: t('Import Pool'), description: LIQUIDITY_DESC, card: 'liquidity' },
      '/farms': {
        basePath: true,
        title: t('Farms: earn CBON with liquidity'),
        description: 'Stake your CADINUdex liquidity positions in farms on BNB Chain and earn CBON rewards.',
        card: 'farms',
      },
      '/pools': {
        basePath: true,
        title: t('Staking Pools: stake CADINU, earn CBON'),
        description: 'Stake CADINU in single-token staking pools on BNB Chain and earn CBON rewards with every block.',
        card: 'pools',
      },
      '/info': {
        basePath: true,
        title: t('Markets: prices, volume and liquidity'),
        description: 'Live prices, volume and liquidity for CADINUdex pools and tokens on BNB Chain.',
        card: 'markets',
      },
      '/tokens': {
        title: t('Tokens'),
        description: 'Price, pools and trading competitions for every token on CADINUdex. Search by name, symbol or address.',
        card: 'tokens',
      },
      '/token': {
        basePath: true,
        title: t('Token'),
        description: 'Price, pools and trading competitions for this token on CADINUdex.',
        card: 'tokens',
      },
      '/cadinu-lock': {
        title: t('CADINU Lock: verified token and LP locks'),
        description:
          'Every liquidity and token lock held by CADINU Lock, with its verified contract, its unlock date and a certificate anyone can check.',
        card: 'lock',
      },
      '/cadinu-lock/create': {
        basePath: true,
        title: t('Create a Lock'),
        description: 'Lock LP tokens, positions or tokens in CADINU Lock from your own wallet and get a certificate anyone can verify.',
        card: 'lock',
      },
      // CAD1 launcher: every /cad1 page is noindex nofollow and out of the sitemap; only the Stage build links to it. "In testing." until a
      // live launcher (v2) is configured (CAD1_LIVE; Bez 2026-10-10: the test is finished)
      '/cad1': {
        title: t('CAD1: launch a token'),
        description: `Launch a token straight into CADINUdex liquidity: token, pool, first buy and a 10-year liquidity lock in one transaction.${CAD1_LIVE ? '' : ' In testing.'}`,
        card: 'home',
      },
      '/cad1/create': {
        title: t('Launch a token with CAD1'),
        description: `Name, symbol and an optional first buy; review every value your wallet will sign.${CAD1_LIVE ? '' : ' In testing.'}`,
        card: 'home',
      },
      '/cad1/launches': {
        title: t('CAD1 launches'),
        description: 'Every token in the CAD1 launcher registry, newest first, with the share bought at launch and the lock date.',
        card: 'home',
      },
      '/lock/verify': {
        title: 'Verify a CADINU Lock certificate',
        description: 'Paste a certificate link, a lock number or a token address and read the lock straight from the verified contract on BNB Chain.',
        card: 'lock',
      },
      '/lock/token/[address]': {
        title: t('Token locks'),
        description: 'Liquidity and token locks of this token held by CADINU Lock.',
        card: 'lock',
      },
      '/trading-competition-v4': COMPETITIONS,
      '/trading-competition': COMPETITIONS,
      '/trading-competition-v4/create': CREATE_COMPETITION,
      '/verified-deploy': {
        title: 'CADINU Verified Deploy',
        description:
          'Deploy a Solidity contract from its artifact with your own wallet, then verify the deployed bytecode against the artifact.',
        card: 'verified-deploy',
      },
      '/u': {
        basePath: true,
        title: t('Profile'),
        description: 'Your CADINU profile: name, NFT avatar, on-chain activity and your referral link.',
        card: 'profile',
      },
      '/profile': {
        basePath: true,
        title: t('Profile'),
        description: 'Your CADINU profile: name, NFT avatar, on-chain activity and your referral link.',
        card: 'profile',
      },
      '/r/[id]': {
        title: t('You are invited'),
        description: 'A friend invited you to CADINUdex: swap, earn CBON and join trading competitions on BNB Chain.',
        card: 'home',
      },
      '/buy-nft': { title: t('CADINU NFTs'), description: 'Choose your character from the CADINU husky NFT collection on BNB Chain.', card: 'nfts' },
      '/governance': {
        basePath: true,
        title: t('Governance'),
        description: 'Read CADINU community proposals and vote with your wallet on BNB Chain.',
        card: 'governance',
      },
      '/prediction-markets': {
        basePath: true,
        title: t('Prediction Markets (demo)'),
        description: 'A preview of prediction markets on CADINUdex. Demo mode: browse markets and outcomes, nothing is traded.',
        card: 'prediction',
      },
      '/legacy': {
        title: t('Legacy products'),
        description: 'Paused CADINU products. Old Lottery prizes stay claimable; V1 to V2 token migration still works.',
        card: 'legacy',
      },
      '/lottery': { title: t('Lottery (paused)'), description: 'The CADINU Lottery is paused. Prizes from the finished rounds stay claimable.', card: 'legacy' },
      // Click to Win left on 2026-10-08 (archive/click-to-win-20261008): /click-to-win redirects to /swap, so it has no meta
      '/migration': { title: t('Token migration'), description: 'Migrate CADINU V1 to CADINU V2.', card: 'legacy' },
      '/terms-of-service': { title: t('Terms of Service'), description: 'The terms of use of CADINUdex.', card: 'home' },
    },
    defaultTitleSuffix: t('CADINUdex'),
  }
}

export const getCustomMeta = memoize(
  (path: string, t: ContextApi['t'], _: string): PageMeta => {
    const pathList = getPathList(t)
    const exact = pathList.paths[path]
    const base = exact ? null : Object.entries(pathList.paths).find(([url, data]) => data.basePath && path.startsWith(`${url}/`))
    const pathMetadata = exact ?? base?.[1]

    if (pathMetadata) {
      // a canonical URL only for a concrete page: never for a dynamic pattern ([id]) or a sub-page matched by its base
      const publicPath = exact ? pathMetadata.publicPath ?? path : null
      return {
        title: `${pathMetadata.title}`,
        ...(pathMetadata.description && { description: pathMetadata.description }),
        ...(pathMetadata.card && { image: ogImageUrl(pathMetadata.card) }),
        ...(publicPath && !publicPath.includes('[') && { url: canonicalUrl(publicPath) }),
      }
    }
    return null
  },
  (path, t, locale) => `${path}#${locale}`,
)
